import type { CSSProperties, ReactNode } from 'react';

/**
 * Full-viewport centered shell used by the public / pre-app screens
 * (login, verify, onboarding, pricing). Dotted-navy desk with a soft brand
 * glow behind the content — the look every onboarding `.dc.html` opens with.
 * Theme-agnostic: everything resolves from tokens, so light mode just works.
 */
export interface CenterShellProps {
  children: ReactNode;
  maxWidth?: number;
  glow?: 'primary' | 'amber' | 'none';
  align?: 'center' | 'start';
}

const GLOWS: Record<'primary' | 'amber' | 'none', string> = {
  primary: 'radial-gradient(640px 320px at 50% -40px, rgba(24,24,27,.04), transparent)',
  amber: 'radial-gradient(640px 320px at 50% -40px, rgba(217,119,6,.06), transparent)',
  none: '',
};

export function CenterShell({ children, maxWidth = 440, glow = 'primary', align = 'center' }: CenterShellProps) {
  const layers = [
    glow === 'none' ? '' : GLOWS[glow],
    'radial-gradient(rgba(24,24,27,.06) 1px, transparent 1px)',
    '#F8F8F7',
  ].filter(Boolean);
  const root: CSSProperties = {
    minHeight: '100vh',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: align === 'center' ? 'center' : 'flex-start',
    justifyContent: 'center',
    padding: '40px 20px',
    background: layers.join(', '),
    backgroundSize: 'auto, 24px 24px, auto',
    color: '#09090B',
    fontFamily: 'var(--font-body)',
  };
  return (
    <div style={root}>
      <div style={{ width: '100%', maxWidth }}>{children}</div>
    </div>
  );
}
